import React, { useMemo } from "react";
import { Species } from "../../../game/Materials";
import { board } from "../../../game/board-helpers";
import { CartLine } from "../../../game/cart";

import { getBoardBuyPrice } from "../../../game/material-values";
import { formatLength, formatMoney } from "../../../utils/formatNumber";
import {
  describeStockDimensionsPlain,
  getMaterialFullName,
  getMaterialState,
  materialMeetsInput,
  nominalSizeLabel,
  speciesLabel,
} from "../../../game/material-helpers";
import {
  LumberChannel,
  LumberSku,
  StoreId,
  unlockedLumberChannels,
} from "../../../game/lumberStock";
import { BoardFaceSvg } from "../../../components/shopping/BoardFaceSvg";
import { CartIcon } from "../../../components/CartIcon";
import { Tooltip } from "../../../components/Tooltip";
import { addToCart } from "../../../sim/commands/cart-commands";
import { useGame, useShopState } from "../../useShell";
import { useCartCount } from "./useStoreTrip";

/**
 * Every rack in town shares one pixels-per-foot, so lengths compare at a
 * glance across bays, channels, and stores. Boards draw at true aspect —
 * this is the whole scale knob, and it's set by the tightest room in
 * town: an 8' board plus the sheet-good shelf under it has to clear the
 * Orange Box wall without scrolling.
 */
const PX_PER_FOOT = 52;

/** How high the safety chain hangs across each bay's boards. */
const CHAIN_HEIGHT_FEET = 3;

// Rack chrome is diegetic shop furniture, so it uses raw wood and steel
// tones rather than the paperwork palette.
const POST_BG = "linear-gradient(90deg, #5f462d, #8a6a45 45%, #4c3823)";
const RAIL_BG = "linear-gradient(180deg, #8a6a45, #4c3823)";
const CHAIN_BG = `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='13' height='8' viewBox='0 0 13 8'><ellipse cx='6.5' cy='4' rx='5.5' ry='3' fill='none' stroke='%23494f52' stroke-width='1.6'/></svg>")`;

/**
 * One store's lumber racks, one rack per purchase channel carried there
 * (see lumberStock.ts). Locked channels are completely absent — new racks
 * appear as reputation grows. No custom cutting anywhere: milling stock
 * down to what a project needs is the game.
 *
 * The racks mimic a real hardwood aisle: one bay per species, boards
 * standing on end and packed shoulder to shoulder, wooden posts between
 * bays, a safety chain across the front, and a paper tag stapled to each
 * board with its dims and price. The board itself is what you pick up —
 * clicking one puts it in the cart, and its tag counts what's in there.
 * Everything reads right to left. The milled state and the channel's
 * flavor line wait in the channel-name tooltip; the surrounding overlay
 * owns the section heading, so each store keeps its own signage.
 */
export const BoardSelector: React.FC<{
  store: StoreId;
}> = ({ store }) => {
  const gameState = useShopState();
  const channels = unlockedLumberChannels(gameState.reputation, store);

  return (
    // One unbroken run of racks, the way an aisle actually stands. A
    // wrapped rack reads as two aisles, so the row never breaks — a
    // window too narrow for every channel scrolls sideways instead.
    <div className="flex items-end justify-center gap-x-2 overflow-x-auto">
      {channels.map((channel) => (
        <LumberChannelSection key={channel.id} channel={channel} />
      ))}
    </div>
  );
};

const channelBoard = (
  channel: LumberChannel,
  sku: LumberSku,
  species: Species,
) =>
  board(species, sku.length, sku.width, sku.thickness, channel.surface, {
    faces: channel.jointedFaces,
    edges: channel.jointedEdges,
  });

const LumberChannelSection: React.FC<{ channel: LumberChannel }> = ({
  channel,
}) => {
  // All of a channel's stock shares one milled state — the header
  // tooltip says it once, alongside the channel's flavor line
  const stateLabel = getMaterialState(
    channelBoard(channel, channel.skus[0], channel.species[0]),
  );

  return (
    // The sign hangs over the rack rather than sitting in the layout:
    // a channel name is wider than the bays it labels, and letting it
    // set the width pushed the next rack away by the overhang. pt
    // reserves the line it needs above the boards.
    <div className="relative shrink-0 pt-6">
      <Tooltip content={`${stateLabel} — ${channel.tagline}`}>
        <h3 className="absolute top-0 left-0 whitespace-nowrap font-condensed font-bold uppercase tracking-wider text-base text-ink-black cursor-help">
          {channel.name}
        </h3>
      </Tooltip>
      <ul className="flex items-end">
        <RackPost />
        {[...channel.species].reverse().map((species) => (
          <React.Fragment key={species}>
            <SpeciesBay species={species} channel={channel} />
            <RackPost />
          </React.Fragment>
        ))}
      </ul>
    </div>
  );
};

/** A wooden upright between bays, running down past the rail to the floor. */
const RackPost: React.FC = () => (
  <li
    aria-hidden
    className="w-1.5 shrink-0 self-stretch"
    style={{ background: POST_BG }}
  />
);

/**
 * One species' bay: every size the channel carries standing shoulder to
 * shoulder (read right to left), a rail at the boards' feet, and a chain
 * across the front with the species name on a paper placard hung from it.
 */
const SpeciesBay: React.FC<{
  species: Species;
  channel: LumberChannel;
}> = ({ species, channel }) => {
  const chainBottom = CHAIN_HEIGHT_FEET * PX_PER_FOOT;
  return (
    <li className="flex flex-col">
      <div className="relative flex items-end justify-center gap-px px-0.5">
        {[...channel.skus].reverse().map((sku) => (
          <BoardForSale
            key={`${sku.length}x${sku.width}x${sku.thickness}`}
            sku={sku}
            species={species}
            channel={channel}
          />
        ))}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 z-10 h-2"
          style={{
            bottom: chainBottom,
            backgroundImage: CHAIN_BG,
            backgroundRepeat: "repeat-x",
          }}
        />
        {/* Species placard hanging from the chain by a short wire */}
        <div
          className="pointer-events-none absolute inset-x-0 z-20 flex flex-col items-center"
          style={{ top: `calc(100% - ${chainBottom + 4}px)` }}
        >
          <span aria-hidden className="h-1.5 w-px bg-[#494f52]" />
          <span className="rounded-[1px] border border-paper-manila-edge bg-paper-manila px-1.5 py-0.5 font-condensed font-bold text-xs uppercase tracking-wide text-ink-black shadow-sm">
            {speciesLabel(species)}
          </span>
        </div>
      </div>
      <div aria-hidden className="h-2 w-full" style={{ background: RAIL_BG }} />
    </li>
  );
};

const BoardForSale: React.FC<{
  sku: LumberSku;
  species: Species;
  channel: LumberChannel;
}> = ({ sku, species, channel }) => {
  const game = useGame();
  const gameState = useShopState();

  const material = useMemo(
    () => channelBoard(channel, sku, species),
    [species, sku, channel],
  );
  const price = getBoardBuyPrice(material, channel.priceMultiplier);
  const fullName = getMaterialFullName(material);
  const line: CartLine = { kind: "material", material, price };
  const inCart = useCartCount(line);

  // The tag distinguishes the boards of a bay — species is on the floor
  const nominal = nominalSizeLabel(material);
  const sizeLine = nominal ?? `${sku.thickness}/4×${sku.width}"`;

  const numberOwned = gameState.player.inventory.filter((m) =>
    materialMeetsInput(m, {
      type: ["board"],
      species: [species],
      length: [sku.length],
      width: [sku.width],
      thickness: [sku.thickness],
      surface: [channel.surface],
      jointedFaces: [channel.jointedFaces],
      jointedEdges: [channel.jointedEdges],
    }),
  ).length;

  const tooltip =
    `${fullName} — ${describeStockDimensionsPlain(material)}` +
    (numberOwned > 0 ? ` — ${numberOwned} in your inventory` : "");

  return (
    <Tooltip content={tooltip}>
      <button
        className="relative block shrink-0 cursor-pointer transition-[filter] hover:brightness-110"
        style={{ height: (sku.length / 12) * PX_PER_FOOT }}
        aria-label={`Add ${fullName} to cart`}
        onClick={() => {
          addToCart(game, line);
        }}
      >
        <BoardFaceSvg
          vertical
          board={material}
          className="block h-full w-auto"
        />
        {/* The paper tag stapled near the board's foot */}
        <span className="absolute left-1/2 bottom-2 z-20 -translate-x-1/2 flex flex-col items-center rounded-[1px] border border-paper-manila-edge bg-paper-ivory px-0.5 py-px font-condensed leading-tight text-ink-black shadow-sm">
          <span className="text-[10px] uppercase whitespace-nowrap">
            {sizeLine}
          </span>
          <span className="text-[10px] whitespace-nowrap">
            {formatLength(sku.length)}
          </span>
          <span className="text-[11px] font-bold tabular-nums whitespace-nowrap">
            {formatMoney(price)}
          </span>
          {numberOwned > 0 && (
            <span
              className={`text-[10px] font-semibold tabular-nums ${
                channel.store === "lumberyard"
                  ? "text-mill-green"
                  : "text-store-orange-dark"
              }`}
            >
              ×{numberOwned}
            </span>
          )}
          {/* What's on the cart already, on the tag beside the count of
              what the shop owns — the two numbers a shopper compares */}
          {inCart > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] font-semibold tabular-nums text-ink-blue">
              <CartIcon />
              {inCart}
            </span>
          )}
        </span>
      </button>
    </Tooltip>
  );
};
